import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";

const mdy = (d) => (d ? `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}` : "");
const num = (n) => (n === null || n === undefined || n === "" ? "" : Math.round(Number(n) * 100) / 100);
const tio = (r) => [r.time_in, r.time_out].filter(Boolean).join(" - ");
const range = (r) => (r.date_to && r.date_to !== r.date ? `${mdy(r.date)} - ${mdy(r.date_to)}` : mdy(r.date));
const remarks = (r) => [r.reason, r.remarks].filter(Boolean).join(" · ");
const BASE = [["Employee ID", (r) => r.employee_no], ["Employee Name", (r) => r.employee_name], ["Date Filed", (r) => mdy(r.date_filed)]];
const TAIL = [["Approved Date", (r) => mdy(r.approved_date)], ["Reason/Remarks", remarks, "reason"], ["Status", (r) => r.status, "status"]];

const REPORTS = {
  leave: { title: "Leave Reports", icon: "CalendarDays", cols: [...BASE, ["Form Type", (r) => r.form_type], ["From", (r) => mdy(r.date_from)], ["To", (r) => mdy(r.date_to)],
    ["With Pay", (r) => r.with_pay], ["Half Day", (r) => r.half_day], ["Leave Count", (r) => num(r.leave_count)], ...TAIL] },
  overtime: { title: "Overtime Reports", icon: "Timer", cols: [...BASE, ["Overtime Date", (r) => mdy(r.date)], ["Overtime Time-In & Out", tio],
    ["OT Requested (hrs)", (r) => num(r.ot_requested)], ["Break (hrs)", (r) => num(r.break_hours)], ["Overtime Approved (hrs)", (r) => num(r.ot_approved)],
    ["Total Approved (hrs)", (r) => num(r.total_approved)], ...TAIL] },
  wfh: { title: "Work From Home Reports", icon: "Home", cols: [...BASE, ["Work From Home Date", range], ["Work From Home Time-In & Out", tio],
    ["Approved Percentage", (r) => (r.approved_pct != null ? `${r.approved_pct}%` : "")], ...TAIL] },
  "official-business": { title: "Official Business Reports", icon: "Briefcase", cols: [...BASE, ["Official Business Date", range],
    ["Official Business Time-In & Out", tio], ...TAIL] },
  "dtr-correction": { title: "DTR Correction Reports", icon: "FileClock", cols: [...BASE, ["Type of DTR Correction", (r) => r.dtr_type],
    ["DTR Correction Date", (r) => mdy(r.date)], ["DTR Correction Time-In", (r) => r.time_in], ["DTR Correction Time-Out", (r) => r.time_out], ...TAIL] },
};
const STATUSES = ["Approved", "Pending", "Cancelled", "Rejected"];
const TONE = { Approved: "bg-emerald-50 text-emerald-700 border-emerald-200", Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Cancelled: "bg-slate-100 text-slate-600 border-slate-200", Rejected: "bg-rose-50 text-rose-700 border-rose-200" };
const EMPTY = { company_id: "all", date_from: "", date_to: "", status: [] };

const exportCsv = (cols, rows, name) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [cols.map(([h]) => h), ...rows.map((r) => cols.map(([, f]) => f(r)))].map((l) => l.map(esc).join(","));
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `${name}.csv`; a.click(); URL.revokeObjectURL(a.href);
};

const Filters = ({ f, setF, companies, onGenerate, onClear, loading }) => {
  const toggle = (s) => setF({ ...f, status: f.status.includes(s) ? f.status.filter((x) => x !== s) : [...f.status, s] });
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 grid gap-3 md:grid-cols-[1.4fr_1fr_1fr_1.8fr_auto] items-end" data-testid="report-filters">
      <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Company</div>
        <Select value={f.company_id} onValueChange={(v) => setF({ ...f, company_id: v })}>
          <SelectTrigger data-testid="report-company-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All my companies</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select></div>
      <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">From</div>
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="report-from" /></div>
      <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">To</div>
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="report-to" /></div>
      <div><div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Status <span className="normal-case font-normal text-slate-400">(none = all)</span></div>
        <div className="flex flex-wrap gap-1.5">{STATUSES.map((s) => (
          <button key={s} type="button" onClick={() => toggle(s)} data-testid={`report-status-${s.toLowerCase()}`}
            className={`px-2.5 h-9 rounded-md border text-xs font-medium transition-colors ${f.status.includes(s) ? TONE[s] + " ring-1 ring-current" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{s}</button>))}</div></div>
      <div className="flex gap-2">
        <Button onClick={onGenerate} disabled={loading} data-testid="report-generate-btn">{loading ? <Icons.Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icons.Play className="h-4 w-4 mr-1.5" />}Generate</Button>
        <Button variant="outline" onClick={onClear} data-testid="report-clear-btn"><Icons.X className="h-4 w-4 mr-1.5" />Clear</Button>
      </div>
    </div>
  );
};

const Cell = ({ col, r }) => {
  const [, fn, kind] = col;
  if (kind === "status") return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-medium ${TONE[r.status] || TONE.Cancelled}`}>{r.status}</span>;
  if (kind === "reason") return (<div className="min-w-[220px] whitespace-normal">{fn(r)}{r.attachment_url && <a href={r.attachment_url} target="_blank" rel="noreferrer" className="block text-blue-600 hover:underline text-xs mt-0.5">View Attachment</a>}</div>);
  return fn(r);
};

export default function HrisReports() {
  const { kind } = useParams();
  const cfg = REPORTS[kind] || REPORTS.leave;
  const Icon = Icons[cfg.icon];
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [size, setSize] = useState(25);
  const [page, setPage] = useState(1);

  useEffect(() => { api.get("/companies").then(({ data: d }) => setCompanies(d)).catch(() => {}); }, []);
  useEffect(() => { setData(null); setF(EMPTY); setQ(""); setPage(1); setSize(25); }, [kind]);

  const generate = async () => {
    setLoading(true);
    try {
      const params = { date_from: f.date_from, date_to: f.date_to, status: f.status.join(","), ...(f.company_id !== "all" ? { company_id: f.company_id } : {}) };
      const { data: d } = await api.get(`/hris/reports/${kind}`, { params });
      setData(d); setPage(1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate report"); }
    setLoading(false);
  };
  const clear = () => { setF(EMPTY); setData(null); setQ(""); setPage(1); };

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const s = q.trim().toLowerCase();
    return s ? all.filter((r) => cfg.cols.some(([, fn]) => String(fn(r) ?? "").toLowerCase().includes(s))) : all;
  }, [data, q, cfg]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const shown = rows.slice((page - 1) * size, page * size);
  const fileName = `${kind}_report_${f.date_from || "all"}_to_${f.date_to || "all"}`;

  return (
    <div className="space-y-4" data-testid={`report-page-${kind}`}>
      <PageHeader title={cfg.title} subtitle="Filter by company, date range and status, then Generate. Export saves the current results as CSV."
        actions={<Button variant="outline" disabled={!rows.length} onClick={() => exportCsv(cfg.cols, rows, fileName)} data-testid="report-export-btn"><Icons.Download className="h-4 w-4 mr-1.5" />Export CSV</Button>} />
      <Filters f={f} setF={setF} companies={companies} onGenerate={generate} onClear={clear} loading={loading} />
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 [&>*:first-child]:col-span-2 sm:[&>*:first-child]:col-span-1" data-testid="report-counts">
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3"><div className="text-[10px] uppercase tracking-wide text-slate-400">Total</div><div className="text-xl font-semibold" data-testid="report-total">{data.total}</div></div>
          {STATUSES.map((s) => <div key={s} className="bg-white rounded-xl border border-slate-200 px-4 py-3"><div className="text-[10px] uppercase tracking-wide text-slate-400">{s}</div><div className="text-xl font-semibold" data-testid={`report-count-${s.toLowerCase()}`}>{data.counts[s] || 0}</div></div>)}
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 text-sm text-slate-600">Show
            <Select value={String(size)} onValueChange={(v) => { setSize(Number(v)); setPage(1); }}><SelectTrigger className="w-20 h-8" data-testid="report-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>entries</div>
          <div className="relative w-full sm:w-64"><Icons.Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input className="pl-8 h-9" placeholder="Search results" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="report-search" /></div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="report-table">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500"><tr>{cfg.cols.map(([h]) => <th key={h} className="px-3 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {!data && <tr><td colSpan={cfg.cols.length} className="px-4 py-14 text-center text-slate-400"><Icon className="h-8 w-8 mx-auto mb-2 text-slate-300" />Choose your filters and click Generate.</td></tr>}
              {data && !shown.length && <tr><td colSpan={cfg.cols.length} className="px-4 py-14 text-center text-slate-400" data-testid="report-empty">No records match these filters.</td></tr>}
              {shown.map((r) => <tr key={r.id} className="hover:bg-slate-50/70" data-testid={`report-row-${r.id}`}>{cfg.cols.map((c) => <td key={c[0]} className="px-3 py-2.5 whitespace-nowrap align-top text-slate-700"><Cell col={c} r={r} /></td>)}</tr>)}
            </tbody>
          </table>
        </div>
        {data && <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span data-testid="report-showing">Showing {rows.length ? (page - 1) * size + 1 : 0} to {Math.min(page * size, rows.length)} of {rows.length} entries</span>
          <div className="flex gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="report-prev">Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="report-next">Next</Button></div>
        </div>}
      </div>
    </div>
  );
}
